import React from 'react';
import { resolveSpanColumns } from 'features/agent-analytics/utils/spanColumns';
import { renderSpanCell } from 'features/agent-analytics/components/spanCells';
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableRowClass,
  tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';

export default function SpansTable({
  rows,
  columnKeys,
  onRowClick,
  onRunClick,
  onSpanClick,
  onAnnotationClick,
  selectedSpanId = null,
  emptyMessage = 'No spans match the current filters.',
  // Tick boxes for "Add to dataset…": a Set of span ids and the toggles. Absent → no column.
  checkedIds = null,
  onToggleChecked = null,
  onToggleAllChecked = null,
}) {
  const columns = React.useMemo(() => resolveSpanColumns(columnKeys), [columnKeys]);
  const cellCallbacks = React.useMemo(
    () => ({ onRunClick, onSpanClick, onAnnotationClick }),
    [onRunClick, onSpanClick, onAnnotationClick],
  );
  const selectable = Boolean(checkedIds && onToggleChecked);
  const allChecked = selectable && rows?.length > 0 && rows.every((r) => checkedIds.has(r.span_id));

  if (!rows || rows.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface text-[12px] text-[color:var(--text-muted)]">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="shrink-0 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] text-left" style={{ fontFamily: 'Inter, sans-serif' }}>
          <thead>
            <tr className={tableHeaderRowClass}>
              {selectable ? (
                <th className={`${tableHeaderCellClass} w-8`}>
                  <input type="checkbox" aria-label={allChecked ? 'Clear the selection' : 'Select all spans shown'} title="Select spans to add them to a dataset" checked={allChecked}
                    onChange={() => onToggleAllChecked?.(!allChecked)} onClick={(e) => e.stopPropagation()} />
                </th>
              ) : null}
              {columns.map((col) => (
                <th key={col.key} className={tableHeaderCellClass}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isSelected = selectedSpanId && row.span_id === selectedSpanId;
              return (
                <tr
                  key={row.span_id}
                  onClick={() => onRowClick?.(row)}
                  className={`${tableRowClass} ${isSelected ? 'bg-surface-hover' : ''}`}
                >
                  {selectable ? (
                    <td className={`${tableDataCellClass} w-8`} onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" title="Select to add to a dataset" aria-label={`Select span ${row.name || row.span_id}`} checked={checkedIds.has(row.span_id)}
                        onChange={() => onToggleChecked(row.span_id)} />
                    </td>
                  ) : null}
                  {columns.map((col) => (
                    <td key={col.key} className={tableDataCellClass}>
                      {renderSpanCell(col.key, row, cellCallbacks)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
